"use client";

import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { Avatar, Box, useMediaQuery } from "@mui/material";
import { cleanName } from "@/lib/utils/nameUtils";
import TopLoadingProgress from "./TopLoadingProgress";

// Cycled as HUD-style annotation callouts once the intro sequence hands off.
// Short sentences, tied to what the platform actually does — not generic filler.
const BENEFITS = [
  "Your profile",
  "Your expertise",
  "Meeting preferences",
  "Your availability",
  "Your request inbox",
  "Your dashboard",
];

function getTimeGreeting(firstName) {
  const hour = new Date().getHours();
  const time =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  return firstName ? `${time}, ${firstName}.` : `${time}.`;
}

export function ProfileOrb({ theme }) {
  const isDark = theme.palette.mode === "dark";
  const orbHighlight = isDark ? "#f8fafc" : "#ffffff";
  const orbMid = isDark ? "#cbd5e1" : "#94a3b8";
  const orbDeep = isDark ? "#64748b" : "#475569";
  const orbEdge = isDark ? "#e2e8f0" : "#64748b";

  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        borderRadius: "50%",
        perspective: "360px",
        background: `radial-gradient(circle at 34% 28%, ${alpha(
          orbHighlight,
          0.96,
        )} 0%, ${alpha(orbMid, 0.9)} 28%, ${alpha(orbDeep, 0.92)} 62%, ${alpha(
          theme.palette.background.default,
          0.98,
        )} 100%)`,
        boxShadow: `inset -14px -18px 24px ${alpha(
          orbDeep,
          0.42,
        )}, inset 10px 8px 18px ${alpha(orbHighlight, 0.32)}, 0 0 22px ${alpha(
          orbMid,
          0.38,
        )}`,
        "&::before": {
          content: '""',
          position: "absolute",
          width: "58%",
          height: "58%",
          top: "-10%",
          left: "-8%",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(
            orbHighlight,
            0.62,
          )} 0%, ${alpha("#ffffff", 0)} 72%)`,
          filter: "blur(4px)",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          inset: "14%",
          borderRadius: "50%",
          border: `1px solid ${alpha(orbEdge, 0.28)}`,
          transform: "rotateZ(-22deg) scaleX(0.72)",
        },
      }}
    />
  );
}

const MEEX_INTRO = "It connects you to anyone!";
const CONNECT_QUESTION = "Curious what Meex does?";
const PROCESS_TRANSITION = "Let's get your profile ready.";

const AVATAR_SIZE = 100;
const DONE_SCALE = 1.7; // how much bigger the avatar grows once the intro finishes
const GROW_EASING = "cubic-bezier(0.4, 0, 0.2, 1)"; // smooth, no overshoot/bounce

// Halo "blob" morph — each of the 8 border-radius corners is picked
// independently (never mirrored), plus an off-center shift so it looks like
// it's growing more from one side than the other. Re-rolled on an irregular
// timer so the motion never settles into a predictable loop.
const BLOB_RADIUS_MIN = 26;
const BLOB_RADIUS_MAX = 74;
const BLOB_MORPH_MS = 1900;
const BLOB_RETICK_MIN_MS = 1300;
const BLOB_RETICK_MAX_MS = 2400;

function randomBlobStep() {
  const corner = () =>
    `${Math.round(
      BLOB_RADIUS_MIN + Math.random() * (BLOB_RADIUS_MAX - BLOB_RADIUS_MIN),
    )}%`;
  return {
    borderRadius: `${corner()} ${corner()} ${corner()} ${corner()} / ${corner()} ${corner()} ${corner()} ${corner()}`,
    x: Math.round(-42 + Math.random() * 84),
    y: Math.round(-42 + Math.random() * 84),
    scale: 0.82 + Math.random() * 0.5,
    rotate: Math.round(-24 + Math.random() * 48),
  };
}

// HUD-style annotation callouts (cycle through BENEFITS once the avatar has grown).
// Leader line is an elbow: a short diagonal off the avatar, then a straight
// horizontal run that continues under the label — like a video-essay caption.
// The diagonal's start offset depends on the avatar's actual rendered size
// (which varies by breakpoint), so it's passed in as a prop rather than fixed here.
const ANNOTATION_DIAGONAL_LENGTH = 46;
const ANNOTATION_DIAGONAL_DRAW_MS = 240;
const ANNOTATION_HORIZONTAL_BASE = 40; // base horizontal run before the label's own width extends it
const ANNOTATION_HORIZONTAL_DRAW_MS = 320;
const ANNOTATION_HOLD_MS = 2700; // how long a callout stays fully visible before fading
const ANNOTATION_FADE_MS = 420; // smooth fade-out duration
const ANNOTATION_GAP_MS = 380; // pause before the next one appears in a new spot

// Candidate clock positions for callouts — 12 and 6 o'clock (straight up/down)
// are excluded since they read poorly next to the status line above.
const ANNOTATION_ANGLE_POOL = Array.from(
  { length: 12 },
  (_, i) => i * 30 - 90,
).filter((deg) => deg !== -90 && deg !== 90);

// Typewriter pacing — deliberately a little uneven, like a person typing.
const CHAR_DELAY_MIN_MS = 38;
const CHAR_DELAY_MAX_MS = 78;
const PUNCTUATION_PAUSE_MS = 180; // brief beat after a comma/period, like natural speech
const HOLD_AFTER_SENTENCE_MS = 1900;
const ERASE_DELAY_MS = 18; // fast backward erase once a sentence has been read
const GLITCH_CHANCE = 0.035; // per character, only once text is underway

/**
 * A single HUD callout: an elbow leader line (short diagonal off the avatar's
 * edge, then a straight horizontal run that continues under the label), with
 * the label typed in above the horizontal segment. Fades smoothly in and out
 * as the `visible` prop toggles — the parent swaps to a new position/text
 * once the fade-out finishes.
 */
function AnnotationCallout({ text, angleDeg, visible, startOffset, theme }) {
  const measureRef = useRef(null);
  const [textWidth, setTextWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );
  const [typedText, setTypedText] = useState("");
  const [mounted, setMounted] = useState(false);

  useLayoutEffect(() => {
    if (measureRef.current) {
      setTextWidth(measureRef.current.offsetWidth);
    }
  }, [text]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isSmallScreen = viewportWidth < 480;
  const isDark = theme.palette.mode === "dark";
  const calloutColor = theme.palette.text.primary;

  useEffect(() => {
    const mountTimer = setTimeout(() => setMounted(true), 20);
    setTypedText(text);

    return () => {
      clearTimeout(mountTimer);
    };
  }, [text]);

  const rad = (angleDeg * Math.PI) / 180;
  const pointsRight = Math.cos(rad) >= 0;
  const bendOffset = startOffset + ANNOTATION_DIAGONAL_LENGTH;
  const bendX = Math.cos(rad) * bendOffset;
  const bendY = Math.sin(rad) * bendOffset;

  const edgeMargin = 24;
  const labelFontSize = isSmallScreen ? "0.8rem" : "1rem";
  const labelMaxWidth = Math.max(
    90,
    viewportWidth / 2 - edgeMargin - Math.abs(bendX),
  );
  const labelWidthPx = isSmallScreen
    ? Math.min(132, Math.max(96, viewportWidth / 2 - startOffset - edgeMargin))
    : Math.min(textWidth ? textWidth + 6 : labelMaxWidth, labelMaxWidth);
  const mobileLabelOffset = startOffset + 4;
  const mobileLabelY = Math.sin(rad) * mobileLabelOffset;
  const horizontalLength = Math.min(
    ANNOTATION_HORIZONTAL_BASE + textWidth,
    labelMaxWidth,
  );

  return (
    <Box
      aria-hidden
      sx={{
        position: "fixed",
        top: "50%",
        left: "50%",
        zIndex: 2147483647,
        pointerEvents: "none",
        opacity: mounted && visible ? 1 : 0,
        transform: mounted && visible ? "translateY(0)" : "translateY(8px)",
        transition: `opacity ${ANNOTATION_FADE_MS}ms ease, transform ${ANNOTATION_FADE_MS}ms ease`,
      }}
    >
      <Box
        sx={{
          display: isSmallScreen ? "none" : "block",
          position: "absolute",
          top: 0,
          left: 0,
          transform: `translate(${Math.cos(rad) * startOffset}px, ${Math.sin(
            rad,
          ) * startOffset}px)`,
        }}
      >
        <Box
          sx={{
            width: `${ANNOTATION_DIAGONAL_LENGTH}px`,
            height: "1.5px",
            backgroundColor: alpha(calloutColor, isDark ? 0.52 : 0.38),
            boxShadow: `0 0 5px ${alpha(calloutColor, isDark ? 0.28 : 0.16)}`,
            transformOrigin: "left center",
            transform: `translateY(-50%) rotate(${angleDeg}deg)`,
            clipPath: "inset(0 100% 0 0)",
            "@keyframes meex-diagonal-draw": {
              "0%": { clipPath: "inset(0 100% 0 0)" },
              "100%": { clipPath: "inset(0 0% 0 0)" },
            },
            animation: `meex-diagonal-draw ${ANNOTATION_DIAGONAL_DRAW_MS}ms ease-out forwards`,
          }}
        />
      </Box>

      <Box
        sx={{
          display: isSmallScreen ? "none" : "block",
          position: "absolute",
          top: 0,
          left: 0,
          transform: `translate(${bendX}px, ${bendY}px)`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: pointsRight ? 0 : "auto",
            right: pointsRight ? "auto" : 0,
            width: `${horizontalLength}px`,
            height: "1.5px",
            backgroundColor: alpha(calloutColor, isDark ? 0.52 : 0.38),
            boxShadow: `0 0 5px ${alpha(calloutColor, isDark ? 0.28 : 0.16)}`,
            transform: "translateY(-50%)",
            clipPath: pointsRight ? "inset(0 100% 0 0)" : "inset(0 0 0 100%)",
            "@keyframes meex-horizontal-draw-right": {
              "0%": { clipPath: "inset(0 100% 0 0)" },
              "100%": { clipPath: "inset(0 0% 0 0)" },
            },
            "@keyframes meex-horizontal-draw-left": {
              "0%": { clipPath: "inset(0 0 0 100%)" },
              "100%": { clipPath: "inset(0 0 0 0%)" },
            },
            animation: `${
              pointsRight
                ? "meex-horizontal-draw-right"
                : "meex-horizontal-draw-left"
            } ${ANNOTATION_HORIZONTAL_DRAW_MS}ms ease-out ${ANNOTATION_DIAGONAL_DRAW_MS}ms forwards`,
          }}
        />
      </Box>

      <Box
        ref={measureRef}
        sx={{
          position: "absolute",
          visibility: "hidden",
          whiteSpace: "nowrap",
          fontSize: labelFontSize,
          fontWeight: 500,
          letterSpacing: "0.01em",
        }}
      >
        {text}
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: isSmallScreen ? `${mobileLabelY}px` : `${bendY - 6}px`,
          bottom: "auto",
          left: isSmallScreen
            ? pointsRight
              ? `${mobileLabelOffset}px`
              : "auto"
            : pointsRight
            ? `${bendX}px`
            : "auto",
          right: isSmallScreen
            ? pointsRight
              ? "auto"
              : `${mobileLabelOffset}px`
            : pointsRight
            ? "auto"
            : `${Math.abs(bendX)}px`,
          transform: isSmallScreen ? "translateY(-50%)" : "translateY(-100%)",
          width: `${labelWidthPx}px`,
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          textAlign: pointsRight ? "left" : "right",
          lineHeight: isSmallScreen ? 1.2 : 1.3,
          fontSize: labelFontSize,
          fontWeight: 500,
          letterSpacing: "0.01em",
          color: calloutColor,
          textShadow: isDark
            ? "0 1px 6px rgba(0, 0, 0, 0.32)"
            : "0 1px 6px rgba(255, 255, 255, 0.72)",
        }}
      >
        {typedText}
      </Box>
    </Box>
  );
}

/**
 * Full-screen enrichment loader: blurred backdrop + a personalized card
 * (plain avatar) with a left-aligned status line that types out a named
 * greeting → connection question → Meex intro → process transition. Once
 * that intro sequence finishes, the text fades and collapses while the
 * avatar grows into a glowing, morphing centerpiece, and BENEFITS lines
 * cycle in as HUD-style annotation callouts (line draws out from the
 * avatar's edge, label fades into place, then fades out before the next
 * one appears in a new spot). Purely presentational — all show/hide logic
 * lives in EnrichmentNotifier.
 */
export default function EnrichmentLoadingScreen({
  showDetails = true,
  userName,
  userImage,
  messageFirstSignup = false,
  onBackdropClick = () => {},
  onIntroTextComplete = () => {},
}) {
  const onIntroTextCompleteRef = useRef(onIntroTextComplete);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const loaderTextColor = theme.palette.text.primary;
  const monoAccent = isDark ? "#e2e8f0" : "#475569";
  const monoSoft = isDark ? "#94a3b8" : "#64748b";
  const loaderLineColor = alpha(loaderTextColor, isDark ? 0.72 : 0.58);
  const loaderBackdrop = isDark
    ? alpha(theme.palette.background.default, 0.52)
    : alpha(theme.palette.background.default, 0.3);
  const loaderTextShadow = isDark
    ? "0 1px 6px rgba(0, 0, 0, 0.34)"
    : "0 1px 6px rgba(255, 255, 255, 0.78)";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const doneScale = isMobile ? 1.15 : DONE_SCALE;
  // Always rendered at this (larger) pixel size, then scaled down for the
  // typing phase — avoids upscaling blur when it later grows to "done".
  const AVATAR_RENDER_PX = Math.round(AVATAR_SIZE * doneScale);
  // Annotation leader lines start right at the avatar's actual edge, so they
  // sit a touch closer on mobile where the avatar renders smaller.
  const annotationStartOffset = isMobile
    ? AVATAR_RENDER_PX / 2 + 2
    : AVATAR_RENDER_PX / 2 + 6;

  const firstName = cleanName(userName)?.split(" ")[0];
  const sequence = useMemo(
    () =>
      messageFirstSignup
        ? [getTimeGreeting(firstName)]
        : [getTimeGreeting(firstName), PROCESS_TRANSITION],
    [firstName, messageFirstSignup],
  );

  useEffect(() => {
    onIntroTextCompleteRef.current = onIntroTextComplete;
  }, [onIntroTextComplete]);

  // Halo blob morph state — re-rolled on an irregular timer (see comment above).
  const [blob, setBlob] = useState(() => randomBlobStep());
  const blobTimerRef = useRef(null);

  useEffect(() => {
    if (!showDetails) return;
    let cancelled = false;

    const tick = () => {
      setBlob(randomBlobStep());
      const nextDelay =
        BLOB_RETICK_MIN_MS +
        Math.random() * (BLOB_RETICK_MAX_MS - BLOB_RETICK_MIN_MS);
      blobTimerRef.current = setTimeout(() => {
        if (!cancelled) tick();
      }, nextDelay);
    };

    blobTimerRef.current = setTimeout(tick, BLOB_RETICK_MIN_MS);
    return () => {
      cancelled = true;
      clearTimeout(blobTimerRef.current);
    };
  }, [showDetails]);

  const [displayedText, setDisplayedText] = useState("");
  const [phase, setPhase] = useState("typing"); // "typing" | "done"
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!showDetails) {
      if (phase !== "done") {
        setDisplayedText("");
        setPhase("typing");
      }
      return;
    }

    if (phase === "done") return;

    let cancelled = false;
    let text = "";
    let seqPos = 0;

    const schedule = (delay, fn) => {
      timeoutRef.current = setTimeout(() => {
        if (!cancelled) fn();
      }, delay);
    };

    const backspaceGlitch = (remaining, target) => {
      if (remaining > 0 && text.length > 0) {
        text = text.slice(0, -1);
        setDisplayedText(text);
        schedule(40, () => backspaceGlitch(remaining - 1, target));
      } else {
        schedule(160, () => typeStep(target));
      }
    };

    // Erases the whole sentence backward (fast) instead of snapping to blank.
    const eraseAll = (onDone) => {
      if (text.length > 0) {
        text = text.slice(0, -1);
        setDisplayedText(text);
        schedule(ERASE_DELAY_MS, () => eraseAll(onDone));
      } else {
        schedule(250, onDone);
      }
    };

    function typeStep(target) {
      if (text.length >= target.length) {
        const isLastSentence = seqPos >= sequence.length - 1;
        if (isLastSentence) {
          onIntroTextCompleteRef.current();
          // Intro is done — hand off to the big glowing avatar, no more looping.
          schedule(HOLD_AFTER_SENTENCE_MS, () => setPhase("done"));
          return;
        }

        schedule(HOLD_AFTER_SENTENCE_MS, () => {
          eraseAll(() => {
            seqPos += 1;
            typeStep(sequence[seqPos]);
          });
        });
        return;
      }

      text += target[text.length];
      setDisplayedText(text);

      // Occasionally "mistype" a couple of characters back, then resume — never
      // on the first/last few characters so it always reads cleanly at rest.
      const canGlitch = text.length > 5 && text.length < target.length - 3;
      if (canGlitch && Math.random() < GLITCH_CHANCE) {
        const backCount = 1 + Math.floor(Math.random() * 2);
        schedule(200, () => backspaceGlitch(backCount, target));
        return;
      }

      // Real typing isn't metronomic — pause a beat longer after punctuation.
      const lastChar = target[text.length - 1];
      const punctuationPause = /[.,!?]/.test(lastChar)
        ? PUNCTUATION_PAUSE_MS
        : 0;
      const delay =
        CHAR_DELAY_MIN_MS +
        Math.random() * (CHAR_DELAY_MAX_MS - CHAR_DELAY_MIN_MS) +
        punctuationPause;
      schedule(delay, () => typeStep(target));
    }

    setPhase("typing");
    schedule(200, () => typeStep(sequence[seqPos]));

    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
    };
  }, [phase, showDetails, sequence]);

  // Once the intro hands off, cycle the reserved BENEFITS lines as HUD
  // callouts: each one shows, holds, fades out smoothly, then the next one
  // appears in a new position around the avatar.
  const [annotation, setAnnotation] = useState(null); // { key, text, angleDeg, visible }
  const annotationTimerRef = useRef(null);

  useEffect(() => {
    if (phase !== "done") {
      setAnnotation(null);
      return;
    }

    let cancelled = false;
    let counter = 0;

    const schedule = (delay, fn) => {
      annotationTimerRef.current = setTimeout(() => {
        if (!cancelled) fn();
      }, delay);
    };

    // Shuffle-bag pickers: every item in the pool gets used exactly once
    // before any repeat, instead of pure random re-rolls (which can clump
    // the same item several times in a row purely by chance).
    function shuffled(list) {
      const arr = [...list];
      for (let i = arr.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    }

    function makeBagPicker(list) {
      let bag = [];
      let lastPicked;
      return () => {
        if (bag.length === 0) {
          bag = shuffled(list);
          // Avoid the same item landing back-to-back across a bag reshuffle.
          if (bag.length > 1 && bag[0] === lastPicked) {
            const swapAt = 1 + Math.floor(Math.random() * (bag.length - 1));
            [bag[0], bag[swapAt]] = [bag[swapAt], bag[0]];
          }
        }
        const item = bag.shift();
        lastPicked = item;
        return item;
      };
    }

    const angleBag = makeBagPicker(ANNOTATION_ANGLE_POOL);
    const textIdxBag = makeBagPicker(BENEFITS.map((_, i) => i));

    const showNext = () => {
      const angleDeg = angleBag();
      const text = BENEFITS[textIdxBag()];
      counter += 1;
      setAnnotation({ key: counter, text, angleDeg, visible: true });

      schedule(ANNOTATION_HOLD_MS, () => {
        setAnnotation((prev) => (prev ? { ...prev, visible: false } : prev));
        schedule(ANNOTATION_FADE_MS, () => {
          schedule(ANNOTATION_GAP_MS, showNext);
        });
      });
    };

    schedule(700, showNext);

    return () => {
      cancelled = true;
      clearTimeout(annotationTimerRef.current);
    };
  }, [phase]);

  return (
    <>
      <TopLoadingProgress />

      {/* Backdrop — stays alive during VanityModal handoff so dashboard doesn't flash */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: loaderBackdrop,
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          zIndex: 2147483645,
          pointerEvents: "auto",
          borderRadius: "0px!important",
          cursor: "default",
          "@keyframes meex-backdrop-in": {
            "0%": { opacity: 0 },
            "100%": { opacity: 1 },
          },
          animation: "meex-backdrop-in 0.35s ease forwards",
        }}
        onClick={onBackdropClick}
      />

      {/* Glow halo — layered, pulsing rings hugging the avatar for a futuristic
          feel. Bigger and already animating during the typing phase, then
          smoothly grows further once the intro hands off to the big avatar.
          Split into an outer (phase-based size/position) + inner (continuous
          organic pulse) box so the two transforms compose instead of fighting. */}
      {showDetails && (
        <Box
          aria-hidden
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            width: "min(620px, 96vw)",
            height: "min(620px, 96vw)",
            zIndex: 2147483645,
            pointerEvents: "none",
            transform:
              phase === "done"
                ? "translate(-50%, -50%) scale(1)"
                : "translate(-50%, -50%) scale(0.62)",
            opacity: phase === "done" ? 0.84 : 0.5,
            transition: `opacity 0.8s ease, transform 0.9s ${GROW_EASING}`,
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: blob.borderRadius,
              background: `radial-gradient(circle at center, ${alpha(
                monoSoft,
                0.95,
              )} 0%, ${alpha(monoSoft, 0.7)} 22%, ${alpha(
                monoAccent,
                0.45,
              )} 42%, ${alpha(monoAccent, 0.12)} 60%, ${alpha(
                monoAccent,
                0,
              )} 76%)`,
              filter: `blur(18px) brightness(${1.1 + (blob.scale - 1) * 0.5})`,
              transform: `translate(${blob.x}px, ${blob.y}px) scale(${blob.scale}) rotate(${blob.rotate}deg)`,
              // Irregular re-rolled target (see randomBlobStep) + a plain
              // transition, instead of a fixed @keyframes loop, so the morph
              // never mirrors itself or repeats on a predictable cycle.
              transition: `border-radius ${BLOB_MORPH_MS}ms ease-in-out, transform ${BLOB_MORPH_MS}ms ease-in-out, filter ${BLOB_MORPH_MS}ms ease-in-out`,
            }}
          />
        </Box>
      )}

      {/* Personalized card: avatar is always dead-center of the screen (same
          anchor point in both phases); the intro status line sits beside it
          on the right instead of stacking below and shifting the center. */}
      {showDetails && (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            zIndex: 2147483646,
            transform: "translate(-50%, -50%)",
            userSelect: "none",
            pointerEvents: "none",
            "@keyframes meex-avatar-in": {
              "0%": {
                opacity: 0,
                transform: "translate(-50%, -50%) translateY(12px)",
              },
              "100%": {
                opacity: 1,
                transform: "translate(-50%, -50%) translateY(0)",
              },
            },
            animation: "meex-avatar-in 0.5s ease forwards",
          }}
        >
          <Box
            sx={{
              position: "relative",
              // Rendered at its full "done" resolution at all times, and only
              // ever scaled DOWN for the smaller typing state — scaling an
              // image UP from a smaller raster is what was causing the blur.
              width: AVATAR_RENDER_PX,
              height: AVATAR_RENDER_PX,
              transform:
                phase === "done" ? "scale(1)" : `scale(${1 / doneScale})`,
              transition: `transform 1s ${GROW_EASING}`,
            }}
          >
            {/* Ring + image together — both breathe as one unit so the border
                never looks static against a pulsing picture. */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                boxShadow:
                  phase === "done"
                    ? `0 0 24px 4px ${alpha(
                        monoSoft,
                        0.4,
                      )}, 0 0 48px 12px ${alpha(monoAccent, 0.22)}`
                    : "none",
                border: `1.5px solid ${alpha(loaderTextColor, 0.65)}`,
                backgroundColor: "transparent",
                p: "2px",
                boxSizing: "border-box",
                // Very mild continuous breathing (ring + image together),
                // independent of phase, plus the typing-only ping ripple.
                // Typing phase pulses a touch more than "done" so it reads
                // clearly on the smaller avatar.
                "@keyframes meex-avatar-pulse": {
                  "0%, 100%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.035)" },
                },
                "@keyframes meex-avatar-pulse-typing": {
                  "0%, 100%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.06)" },
                },
                "@keyframes meex-border-pulse": {
                  "0%, 100%": {
                    boxShadow: `0 0 0 0 ${alpha(loaderTextColor, 0.28)}`,
                  },
                  "50%": {
                    boxShadow: `0 0 0 5px ${alpha(loaderTextColor, 0)}`,
                  },
                },
                animation:
                  phase === "done"
                    ? "meex-avatar-pulse 5.5s ease-in-out infinite"
                    : "meex-avatar-pulse-typing 4.5s ease-in-out infinite, meex-border-pulse 2.2s ease-in-out infinite",
              }}
            >
              {userImage ? (
                <Avatar
                  src={userImage}
                  sx={{
                    width: "100%",
                    height: "100%",
                    fontSize: "1.75rem",
                    fontWeight: 700,
                    bgcolor: "text.primary",
                    color: "background.paper",
                  }}
                />
              ) : (
                <ProfileOrb theme={theme} />
              )}
            </Box>
          </Box>

          {/* Status line — sits to the right of the avatar, vertically centered
              on it. Purely fixed-position, so an empty (mid-erase) string never
              shifts anything. Fades out once the intro finishes. */}
          <Box
            sx={{
              position: "absolute",
              top: isMobile ? "calc(100% + 10px)" : "50%",
              left: isMobile
                ? "50%"
                : `${AVATAR_RENDER_PX / 2 +
                    (phase === "done"
                      ? AVATAR_RENDER_PX / 2
                      : AVATAR_SIZE / 2) +
                    10}px`,
              transform: isMobile ? "translateX(-50%)" : "translateY(-50%)",
              width: isMobile ? "min(200px, 64vw)" : "min(420px, 60vw)",
              maxWidth: isMobile ? "200px" : "420px",
              textAlign: isMobile ? "center" : "left",
              opacity: phase === "done" ? 0 : 1,
              transition: `opacity 0.45s ease, left 1s ${GROW_EASING}, top 0.45s ease`,
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                borderRight: isMobile ? "none" : `1px solid ${loaderLineColor}`,
                fontSize: "clamp(0.9rem, 2.8vw, 1.05rem)",
                fontWeight: 600,
                letterSpacing: "0.02em",
                lineHeight: 1.35,
                color: loaderTextColor,
                textShadow: loaderTextShadow,
                "@keyframes meex-caret": {
                  "50%": { borderColor: "transparent" },
                },
                // ~530ms per phase matches the native OS caret blink rate
                animation: isMobile
                  ? "none"
                  : "meex-caret 1.06s step-end infinite",
              }}
            >
              {displayedText}
            </Box>
          </Box>
        </Box>
      )}

      {/* HUD annotation callout — line draws from the avatar's edge and runs
          under the typed label; fades out smoothly before the next appears
          in a new spot around the avatar */}
      {showDetails && annotation && (
        <AnnotationCallout
          key={annotation.key}
          text={annotation.text}
          angleDeg={annotation.angleDeg}
          visible={annotation.visible}
          startOffset={annotationStartOffset}
          theme={theme}
        />
      )}
    </>
  );
}
